/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "PagerDuty", description = "The PagerDuty extension lets Hightouch notify your on-call staff about failed queries and syncs.", lastUpdatedDate = new Date(1787184000000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "PagerDuty",
        description: "The PagerDuty extension lets Hightouch notify your on-call staff about failed queries and syncs.",
        lastUpdatedDate: "Thu Aug 20 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Setup",
          "depth": 0,
          "id": "setup"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      a: "a",
      h2: "h2",
      ol: "ol",
      li: "li",
      ul: "ul",
      strong: "strong",
      img: "img"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.p, {
        children: "The PagerDuty extension lets Hightouch notify your on-call staff about failed queries and syncs."
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've authorized access to PagerDuty, you must ", _jsx(_components.a, {
          href: "/syncs/alerting#configure-alerts",
          children: "set up alerts for each sync"
        }), " that you want to send alerts through PagerDuty on."]
      }), "\n", _jsx(_components.h2, {
        id: "setup",
        children: "Setup"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Create a PagerDuty Events API V2 extension and copy its API Key:", "\n", _jsxs(_components.ul, {
            children: ["\n", _jsxs(_components.li, {
              children: ["In PagerDuty, go to the ", _jsx(_components.strong, {
                children: "Integrations"
              }), " tab in the ", _jsx(_components.strong, {
                children: "Services"
              }), " page. Select ", _jsx(_components.strong, {
                children: "+ Add another Integration"
              }), ".\n", _jsx(_components.img, {
                src: "integrations/pagerduty-add-integration.png",
                alt: "Adding an integration in PagerDuty"
              })]
            }), "\n", _jsxs(_components.li, {
              children: ["Select ", _jsx(_components.strong, {
                children: "Events API V2"
              }), " integration and click ", _jsx(_components.strong, {
                children: "Add"
              }), ".\n", _jsx(_components.img, {
                src: "integrations/pagerduty-events-API-V2.png",
                alt: "Events API V2 Integration"
              })]
            }), "\n", _jsxs(_components.li, {
              children: ["Back in the ", _jsx(_components.strong, {
                children: "Integrations"
              }), " tab, copy the ", _jsx(_components.strong, {
                children: "Integration Key"
              }), ".\n", _jsx(_components.img, {
                src: "integrations/pagerduty-integration-key.png",
                alt: "Copying the Integration Key in PagerDuty"
              })]
            }), "\n"]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["In Hightouch, go to the ", _jsxs(_components.a, {
            href: "https://app.hightouch.com/extensions?from=docs",
            children: [_jsx(_components.strong, {
              children: "Extensions"
            }), " page"]
          }), " and select ", _jsx(_components.a, {
            href: "https://app.hightouch.com/extensions/alerting?from=docs",
            children: _jsx(_components.strong, {
              children: "Alerting"
            })
          }), ". Open the ", _jsxs(_components.a, {
            href: "https://app.hightouch.com/extensions/alerting/configuration?from=docs",
            children: [_jsx(_components.strong, {
              children: "Configuration"
            }), " tab"]
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Set up"
          }), " under PagerDuty and paste the ", _jsx(_components.strong, {
            children: "PagerDuty API Key"
          }), " you previously copied."]
        }), "\n", _jsxs(_components.li, {
          children: ["In the ", _jsxs(_components.a, {
            href: "https://app.hightouch.com/extensions/alerting/configuration?from=docs",
            children: [_jsx(_components.strong, {
              children: "Configuration"
            }), " tab"]
          }), ", click ", _jsx(_components.strong, {
            children: "Add alert"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["In the modal that appears, enter a descriptive name for your alert, select ", _jsx(_components.strong, {
            children: "PagerDuty"
          }), " as the ", _jsx(_components.strong, {
            children: "Platform"
          }), " and select the ", _jsx(_components.strong, {
            children: "Credentials"
          }), " you just entered."]
        }), "\n", _jsxs(_components.li, {
          children: ["Set the ", _jsx(_components.strong, {
            children: "Default Behaviors"
          }), ", and optionally enter a ", _jsx(_components.strong, {
            children: "Minimum Alert Interval"
          }), ".", "\n", _jsxs(_components.ul, {
            children: ["\n", _jsxs(_components.li, {
              children: ["The ", _jsx(_components.strong, {
                children: "Default Behaviors"
              }), " can be ", _jsx(_components.strong, {
                children: "fatal error"
              }), " and/or ", _jsx(_components.strong, {
                children: "row error"
              }), ".", "\n", _jsxs(_components.ul, {
                children: ["\n", _jsxs(_components.li, {
                  children: ["A ", _jsx(_components.strong, {
                    children: "fatal error"
                  }), " is an error communicating with either a source or a destination. it's 'fatal' because the sync can't even run."]
                }), "\n", _jsxs(_components.li, {
                  children: ["A ", _jsx(_components.strong, {
                    children: "row error"
                  }), " means a problem with a particular row or rows. The sync works, but one or more rows failed."]
                }), "\n"]
              }), "\n"]
            }), "\n", _jsxs(_components.li, {
              children: ["Setting the ", _jsx(_components.strong, {
                children: "Minimum Alert Interval"
              }), " lets you receive alerts at spaced intervals—once every hour, for instance. If you don't set this, you receive alerts every time your selected error occurs. You must enter the Minimum Alert Interval in minutes."]
            }), "\n"]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["Once you've entered your alert preferences, click ", _jsx(_components.strong, {
            children: "Create"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/syncs/alerting#configure-alerts",
            children: "Set up alerts for each sync"
          }), " that you want to send alerts through PagerDuty on."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Now when a sync attempt fails, Hightouch sends an alert to your PagerDuty service."
      })]
    });
  }
}
export default MDXContent;
